/**
 * DealListPane — Cockpit Component
 * Left pane (360px fixed) rendering filtered, grouped, priority-ordered rows.
 * Groups: Strong → Moderate/Weak → Pass (collapsible, collapsed by default).
 *
 * Authority: WI-UI-CLEANUP-001 Stage 2.
 * Token sources: ui-invariants.js (DS_TYPOGRAPHY, DS_COLORS, DS_BORDERS)
 * Primitives: shadcn Collapsible, shadcn ScrollArea
 */

import React, { useState, useCallback, useRef } from 'react';
import { DS_TYPOGRAPHY, DS_COLORS, DS_BORDERS } from '@/config/ui-invariants';
import { COCKPIT_LABELS } from '@/config/task-display-config';
import { deriveVerdictPhrase } from '@/lib/verdictPhrase';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import { DealListRow } from './DealListRow';

// ──────────────────────────────────────────────────────────────────────────────
// Filter logic
// ──────────────────────────────────────────────────────────────────────────────

const PASS_THROUGH_TILES = new Set(['oldest', 'overnight', 'kb_writes']);

function applyFilterTile(items, filterTile) {
  if (!filterTile || PASS_THROUGH_TILES.has(filterTile)) return items;

  return items.filter((item) => {
    const { tone } = deriveVerdictPhrase(item);
    switch (filterTile) {
      case 'strong':      return tone === 'strong';
      case 'weak':        return tone === 'weak' || tone === 'moderate';
      case 'pass':        return tone === 'unclear';
      case 'directional': return tone === 'weak';
      default:            return true;
    }
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Group header
// ──────────────────────────────────────────────────────────────────────────────

function GroupHeader({ label }) {
  return (
    <div
      style={{
        ...DS_TYPOGRAPHY.microLabel,
        color: DS_COLORS.text.muted,
        padding: '12px 16px 4px',
      }}
    >
      {label}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Skeleton rows (loading state)
// ──────────────────────────────────────────────────────────────────────────────

function SkeletonRow({ idx }) {
  return (
    <div
      key={idx}
      style={{
        height: 80,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 12,
        borderLeft: `3px solid ${DS_COLORS.border.default}`,
        marginBottom: 1,
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div
          style={{
            height: 14,
            borderRadius: DS_BORDERS.radiusCard,
            background: DS_COLORS.surface.slate,
            width: '60%',
          }}
        />
        <div
          style={{
            height: 12,
            borderRadius: DS_BORDERS.radiusCard,
            background: DS_COLORS.surface.slate,
            width: '40%',
          }}
        />
      </div>
      <div
        style={{
          height: 22,
          width: 90,
          borderRadius: DS_BORDERS.radiusPill,
          background: DS_COLORS.surface.slate,
        }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// DealListPane
// ──────────────────────────────────────────────────────────────────────────────

/**
 * @param {{
 *   items: Array<object>,
 *   selectedId: string | null,
 *   onSelect: (id: string) => void,
 *   filterTile: string | null,
 *   loading: boolean,
 *   error: string | null,
 * }} props
 */
export function DealListPane({
  items = [],
  selectedId,
  onSelect,
  filterTile = null,
  loading = false,
  error = null,
}) {
  const [passOpen, setPassOpen] = useState(false);

  // Derive tone for every item once, then partition into groups
  const annotated = React.useMemo(
    () =>
      applyFilterTile(items, filterTile).map((item) => ({
        item,
        ...deriveVerdictPhrase(item),
      })),
    [items, filterTile],
  );

  const strongGroup   = annotated.filter((a) => a.tone === 'strong');
  const midGroup      = annotated.filter((a) => a.tone === 'moderate' || a.tone === 'weak');
  const passGroup     = annotated.filter((a) => a.tone === 'unclear');

  // ── Keyboard navigation ──────────────────────────────────────────────────
  // Build a flat ordered list of visible item IDs (pass group only if open).
  const visibleIds = React.useMemo(() => {
    const ids = [
      ...strongGroup.map((a) => a.item.id),
      ...midGroup.map((a) => a.item.id),
      ...(passOpen ? passGroup.map((a) => a.item.id) : []),
    ];
    return ids;
  }, [strongGroup, midGroup, passGroup, passOpen]);

  const listboxRef = useRef(null);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!['ArrowUp', 'ArrowDown'].includes(e.key)) return;
      e.preventDefault();
      const currentIdx = visibleIds.indexOf(selectedId);
      let nextIdx;
      if (e.key === 'ArrowDown') {
        nextIdx = currentIdx < visibleIds.length - 1 ? currentIdx + 1 : currentIdx;
      } else {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : 0;
      }
      if (nextIdx >= 0 && nextIdx < visibleIds.length) {
        onSelect(visibleIds[nextIdx]);
      }
    },
    [visibleIds, selectedId, onSelect],
  );

  // ── Loading state ───────────────────────────────────────────────────────
  if (loading && items.length === 0) {
    return (
      <div
        data-testid="deal-list-pane"
        style={{ width: 360, height: '100%', borderRight: `1px solid ${DS_COLORS.border.default}` }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <SkeletonRow key={i} idx={i} />
        ))}
      </div>
    );
  }

  // ── Error state ─────────────────────────────────────────────────────────
  if (error) {
    return (
      <div
        data-testid="deal-list-pane"
        style={{
          width: 360,
          height: '100%',
          borderRight: `1px solid ${DS_COLORS.border.default}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 14,
            color: DS_COLORS.text.error,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  // ── Empty state ─────────────────────────────────────────────────────────
  if (annotated.length === 0 && !loading) {
    return (
      <div
        data-testid="deal-list-pane"
        style={{
          width: 360,
          height: '100%',
          borderRight: `1px solid ${DS_COLORS.border.default}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 16,
            color: DS_COLORS.text.muted,
          }}
        >
          Inbox clear
        </div>
      </div>
    );
  }

  // ── Normal render ───────────────────────────────────────────────────────
  return (
    <div
      data-testid="deal-list-pane"
      style={{
        width: 360,
        height: '100%',
        borderRight: `1px solid ${DS_COLORS.border.default}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ScrollArea
        role="listbox"
        aria-label={COCKPIT_LABELS.aria.list}
        ref={listboxRef}
        onKeyDown={handleListKeyDown}
        style={{ flex: 1 }}
      >
        {/* ── Strong group ─────────────────────────────────────────────── */}
        {strongGroup.length > 0 && (
          <div>
            <GroupHeader label={COCKPIT_LABELS.listGroups.worthACall} />
            {strongGroup.map(({ item }) => (
              <DealListRow
                key={item.id}
                item={item}
                selected={selectedId === item.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}

        {/* ── Moderate / Weak group ────────────────────────────────────── */}
        {midGroup.length > 0 && (
          <div>
            <GroupHeader label={COCKPIT_LABELS.listGroups.inReview} />
            {midGroup.map(({ item }) => (
              <DealListRow
                key={item.id}
                item={item}
                selected={selectedId === item.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}

        {/* ── Pass group (collapsible) ─────────────────────────────────── */}
        {passGroup.length > 0 && (
          <Collapsible open={passOpen} onOpenChange={setPassOpen}>
            <CollapsibleTrigger asChild>
              <button
                style={{
                  ...DS_TYPOGRAPHY.microLabel,
                  color: DS_COLORS.text.muted,
                  padding: '12px 16px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                }}
                aria-expanded={passOpen}
                data-testid="pass-group-header"
              >
                <span
                  style={{
                    display: 'inline-block',
                    transform: passOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 200ms ease',
                    fontSize: 10,
                  }}
                >
                  ▶
                </span>
                {passGroup.length} passes cleared
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {passGroup.map(({ item }) => (
                <DealListRow
                  key={item.id}
                  item={item}
                  selected={selectedId === item.id}
                  onSelect={onSelect}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </ScrollArea>
    </div>
  );
}

export default DealListPane;
